import {Button, Input, TabsGroup, TabsItem} from "@atomaro/ui-kit";
import Modal from "@atomaro/ui-kit/components/Modal/Modal";
import {useApiariesStore} from "entities/apiaries";
import {useCurrentHiveStore} from "entities/currentHive";
import {useDevicesStore} from "entities/devices/model/store";
import {useNewDeviceModalStore} from "entities/newDeviceModal";
import {useUserStore} from "entities/user";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {$api} from "shared/api";
import {CloseIcon} from "shared/ui/icons";
import {
    decodeWithZXing,
    drawToCanvas,
    enhance,
    extractId,
    loadImage,
    tryBarcodeDetector,
    tryJsQR,
} from "widgets/newDeviceModal/lib/qr";
import {cleanDash, formatBytes} from "widgets/newDeviceModal/lib/utils";
import {NewDeviceSuccess} from "widgets/newDeviceModal/ui/newDeviceSuccess.tsx";
import {
    DeviceModalFooter,
    DeviceModalHeaderRow,
    DeviceModalMain,
    DeviceModalSubtitle,
    DeviceModalWrapper,
    DeviceTabsWrapper,
    DropZone,
    DropZoneInner,
    ErrorText,
    FileChip,
    FileChipLeft,
    FileChipRight,
    FileName,
    FileSize,
    FileThumb,
    FooterLeft,
    FooterStatus,
    FooterStatusOk,
    HiddenFileInput,
    HintText,
    InputWrapper,
    PreviewBox,
    RemoveFileBtn,
    Spinner,
    SpinnerWrap,
    UploadArea,
} from "widgets/newDeviceModal/ui/styles.ts";
import {useResize} from "../../../app/lib/hooks/useResize.ts";

export const NewDeviceModal = () => {
    const isOpen = useNewDeviceModalStore((s) => s.isOpen);
    const hiveId = useNewDeviceModalStore((s) => s.hiveId);
    const hiveNumber = useNewDeviceModalStore((s) => s.hiveNumber);
    const close = useNewDeviceModalStore((s) => s.close);

    const isScreenMd = useResize()

    const apiaryId = useApiariesStore((s) => s.currentApiaryId);
    const userId = useUserStore((s) => s.userInfo)?.id;

    const reloadDevices = useDevicesStore((s) => s.reload);
    const fetchHiveById = useCurrentHiveStore((s) => s.fetchById);

    const [currentTabIndex, setCurrentTabIndex] = useState<string>("1");

    const [dragOver, setDragOver] = useState(false);
    const [file, setFile] = useState<File | null>(null);
    const [fileUrl, setFileUrl] = useState<string>("");
    const [decoding, setDecoding] = useState(false);
    const [qrId, setQrId] = useState<string>("");
    const [errorMsg, setErrorMsg] = useState<string>("");

    const [manualKey, setManualKey] = useState<string>("");

    const [submitError, setSubmitError] = useState<string>("");

    const [showSuccess, setShowSuccess] = useState(false);

    const inputRef = useRef<HTMLInputElement | null>(null);

    const clearSubmitError = useCallback(() => setSubmitError(""), []);

    const prevUrlRef = useRef<string>("");

    useEffect(() => {
        const prev = prevUrlRef.current;
        if (prev && prev !== fileUrl) {
            try {
                URL.revokeObjectURL(prev);
            } catch {
                // error
            }
        }
        prevUrlRef.current = fileUrl;

        return () => {
            if (prevUrlRef.current) {
                try {
                    URL.revokeObjectURL(prevUrlRef.current);
                } catch {
                    // error
                }
                prevUrlRef.current = "";
            }
        };
    }, [fileUrl]);

    const resetUpload = useCallback(() => {
        setFile(null);
        setFileUrl("");
        setQrId("");
        setErrorMsg("");
        setDecoding(false);
        clearSubmitError();
        if (inputRef.current) inputRef.current.value = "";
    }, [clearSubmitError]);

    const resetManual = useCallback(() => {
        setManualKey("");
        clearSubmitError();
    }, [clearSubmitError]);

    useEffect(() => {
        if (!isOpen) {
            resetUpload();
            resetManual();
            setCurrentTabIndex("1");
            setShowSuccess(false);
        }
    }, [isOpen, resetManual, resetUpload]);

    useEffect(() => {
        resetUpload();
        resetManual();
    }, [currentTabIndex, resetManual, resetUpload]);

    const doDecode = useCallback(
        async (url: string) => {
            setDecoding(true);
            setErrorMsg("");
            setQrId("");
            clearSubmitError();

            try {
                const text = await decodeWithZXing(url);
                const id = extractId(text);
                if (id) {
                    setQrId(id);
                    setDecoding(false);
                    return;
                }
            } catch {
                // error
            }

            try {
                const baseImg = await loadImage(url);
                const rotations = [0, 90, 180, 270];
                const scales = [1, 0.85, 0.7, 0.55];
                const grid = [1, 2, 3];

                for (const rot of rotations) {
                    for (const sc of scales) {
                        const full = enhance(drawToCanvas(baseImg, rot, sc));
                        const bd = await tryBarcodeDetector(full);
                        if (bd) {
                            const id = extractId(bd);
                            if (id) {
                                setQrId(id);
                                setDecoding(false);
                                return;
                            }
                        }
                        const js = tryJsQR(full);
                        if (js) {
                            const id = extractId(js);
                            if (id) {
                                setQrId(id);
                                setDecoding(false);
                                return;
                            }
                        }
                        for (const g of grid) {
                            const cols = g,
                                rows = g;
                            const tileW = Math.floor(baseImg.width / cols);
                            const tileH = Math.floor(baseImg.height / rows);
                            for (let r = 0; r < rows; r++) {
                                for (let c = 0; c < cols; c++) {
                                    const crop = { x: c * tileW, y: r * tileH, w: tileW, h: tileH };
                                    const tile = enhance(drawToCanvas(baseImg, rot, sc, crop));
                                    const js2 = tryJsQR(tile);
                                    if (js2) {
                                        const id = extractId(js2);
                                        if (id) {
                                            setQrId(id);
                                            setDecoding(false);
                                            return;
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                setErrorMsg("Не удалось распознать QR, попробуйте отсканировать файл снова");
            } catch {
                setErrorMsg("Не удалось распознать QR, попробуйте отсканировать файл снова");
            } finally {
                setDecoding(false);
            }
        },
        [clearSubmitError]
    );

    const handleFiles = useCallback(
        (list: FileList | null) => {
            if (!list || !list[0]) return;
            const f = list[0];
            if (!/^image\/(png|jpe?g)$/i.test(f.type)) {
                setErrorMsg("Подходят только форматы jpg, jpeg и png");
                return;
            }
            setFile(f);
            const url = URL.createObjectURL(f);
            setFileUrl(url);
            void doDecode(url);
        },
        [doDecode]
    );

    const onInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            handleFiles(e.target.files);
        },
        [handleFiles]
    );

    const onDrop = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
            setDragOver(false);
            handleFiles(e.dataTransfer.files);
        },
        [handleFiles]
    );

    const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(true);
    }, []);

    const onDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
    }, []);

    const connectDisabled = useMemo(() => {
        if (currentTabIndex === "1") return !qrId || decoding;
        const clean = manualKey;
        return clean.length < 4;
    }, [currentTabIndex, decoding, manualKey, qrId]);

    const onManualChange = (val: string) => {
        // const grouped = groupEvery4(onlyHexAndDash(val));
        setManualKey(val);
        clearSubmitError();
    };

    const refreshData = useCallback(async () => {
        try {
            await reloadDevices();
        } catch {
            // error
        }
        try {
            if (hiveId) await fetchHiveById(String(hiveId));
        } catch {
            // error
        }
    }, [reloadDevices, fetchHiveById, hiveId]);

    const onConnect = async () => {
        clearSubmitError();
        const bsdId = currentTabIndex === "1" ? qrId : manualKey;
        if (!bsdId) return;

        const body: {
            bsdId: string;
            clientId?: string;
            hiveId?: string;
            hiveNumber?: number;
            apiaryId?: string;
        } = {
            bsdId,
            clientId: userId ? String(userId) : undefined,
            hiveId: hiveId ? String(hiveId) : undefined,
            hiveNumber: hiveNumber ? Number(hiveNumber) : undefined,
            apiaryId: apiaryId ? String(apiaryId) : undefined,
        };

        try {
            await $api.post("/collector/devices", body);
            if (window.ym) window.ym(111273960,'reachGoal','connect_device')
            setShowSuccess(true);
        } catch (err: any) {
            const status = err?.response?.status as number | undefined;
            const backendMsg = err?.response?.data?.message || err?.response?.data?.error || err?.message;
            if (status === 404) setSubmitError("Не найдено устройство");
            else if (status === 500) setSubmitError("Ошибка при добавлении устройства");
            else setSubmitError(backendMsg ? String(backendMsg) : "Не удалось подключить устройство");
        }
    };

    const handleSuccessClose = async () => {
        await refreshData();
        close();
        setShowSuccess(false);
    };

    const handleSuccessReturn = async () => {
        await refreshData();
        setShowSuccess(false);
        resetUpload();
        resetManual();
        setCurrentTabIndex("1");
    };

    return (
        <>
            <Modal
                isOpen={isOpen && !showSuccess}
                centered
                style={{ padding: 0, maxHeight: "100%", width: "90%", maxWidth: "720px" }}
                onClickOverlay={close}
            >
                <DeviceModalWrapper>
                    <DeviceModalHeaderRow>
                        <h3>Подключение нового устройства</h3>
                        <svg onClick={close} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="24.000000" height="24.000000" fill="none">
                            <rect width="24" height="24" x="0" y="0" />
                            <path d="M10.9401 12.0006L4.99902 17.9417L6.05968 19.0023L12.0008 13.0612L17.9418 19.0023L19.0025 17.9417L13.0614 12.0006L19.0025 6.0595L17.9418 4.99884L12.0008 10.9399L6.0597 4.99884L4.99904 6.0595L10.9401 12.0006Z" fill="rgb(16,24,40)" fillRule="evenodd" />
                        </svg>
                    </DeviceModalHeaderRow>
                    <DeviceModalSubtitle>
                        Для подключения отсканируйте QR-код на устройстве или введите ключ-идентификатор вручную.
                    </DeviceModalSubtitle>
                    <DeviceModalMain>
                        <DeviceTabsWrapper>
                            <TabsGroup size="m" variant="primary" value={currentTabIndex} onChange={(val) => setCurrentTabIndex(val)}>
                                <TabsItem index="1" label="Загрузить фото" />
                                <TabsItem index="2" label="Ввести ключ вручную" />
                            </TabsGroup>
                        </DeviceTabsWrapper>

                        {currentTabIndex === "1" && (
                            <UploadArea>
                                {!file && (
                                    <>
                                        <DropZone
                                            $dragOver={dragOver}
                                            onDragOver={onDragOver}
                                            onDragLeave={onDragLeave}
                                            onDrop={onDrop}
                                            onClick={() => inputRef.current?.click()}
                                        >
                                            <DropZoneInner>Выберите файл или перетащите его сюда</DropZoneInner>
                                        </DropZone>
                                        <HintText>Подходят только форматы jpg, jpeg и png</HintText>
                                        <HiddenFileInput ref={inputRef} type="file" accept="image/png,image/jpeg" onChange={onInputChange} />
                                    </>
                                )}

                                {!!file && (
                                    <>
                                        <FileChip>
                                            <FileChipLeft>
                                                {!!fileUrl && <FileThumb src={fileUrl} alt="preview" />}
                                                <FileName>{file.name}</FileName>
                                                <FileSize>{formatBytes(file.size)}</FileSize>
                                            </FileChipLeft>
                                            <FileChipRight>
                                                <RemoveFileBtn onClick={resetUpload} aria-label="remove-file">
                                                    <CloseIcon />
                                                </RemoveFileBtn>
                                            </FileChipRight>
                                        </FileChip>

                                        {!decoding && !!errorMsg && !qrId && (
                                            <PreviewBox>
                                                <ErrorText>{errorMsg}</ErrorText>
                                            </PreviewBox>
                                        )}
                                    </>
                                )}
                            </UploadArea>
                        )}

                        {currentTabIndex === "2" && (
                            <UploadArea>
                                <InputWrapper>
                                    <Input
                                        name="manual-bsdid"
                                        value={manualKey}
                                        size="l"
                                        label="Ключ устройства"
                                        placeholder="xxxx-xxxx-xxxx-xxxx"
                                        onChange={(e) => onManualChange(e.target.value)}
                                    />
                                </InputWrapper>
                                <HintText>Можно найти на задней крышке устройства</HintText>
                            </UploadArea>
                        )}
                        {
                            isScreenMd && (
                                <FooterLeft>
                                    {submitError ? (
                                        <ErrorText>{submitError}</ErrorText>
                                    ) : (
                                        <>
                                            {currentTabIndex === "1" && decoding && (
                                                <>
                                                    <SpinnerWrap>
                                                        <Spinner />
                                                    </SpinnerWrap>
                                                    <FooterStatus />
                                                </>
                                            )}
                                            {currentTabIndex === "1" && !decoding && !!qrId && <FooterStatusOk>Устройство обнаружено</FooterStatusOk>}
                                            {currentTabIndex === "2" && <FooterStatus>{cleanDash(manualKey).length >= 4 ? "Готово к подключению" : ""}</FooterStatus>}
                                        </>
                                    )}
                                </FooterLeft>
                            )
                        }
                    </DeviceModalMain>
                    <DeviceModalFooter>
                        {
                            !isScreenMd && (
                                <FooterLeft>
                                    {submitError ? (
                                        <ErrorText>{submitError}</ErrorText>
                                    ) : (
                                        <>
                                            {currentTabIndex === "1" && decoding && (
                                                <>
                                                    <SpinnerWrap>
                                                        <Spinner />
                                                    </SpinnerWrap>
                                                    <FooterStatus />
                                                </>
                                            )}
                                            {currentTabIndex === "1" && !decoding && !!qrId && <FooterStatusOk>Устройство обнаружено</FooterStatusOk>}
                                            {currentTabIndex === "2" && <FooterStatus>{cleanDash(manualKey).length >= 4 ? "Готово к подключению" : ""}</FooterStatus>}
                                        </>
                                    )}
                                </FooterLeft>
                            )
                        }

                        <Button size={"m"} colorScheme={"neutral"} variant={"ghost"} label={"Отменить"} onClick={close} />
                        <Button size={"m"} disabled={connectDisabled} label={"Подключить"} onClick={onConnect} />
                    </DeviceModalFooter>
                </DeviceModalWrapper>
            </Modal>

            {showSuccess && (
                <NewDeviceSuccess onclose={handleSuccessClose} onreturn={handleSuccessReturn} />
            )}
        </>
    );
};
